import type { DispatchParseField } from '../../api/dispatchParse';
import type { DraftCall } from './parseCall';

/** Where a chat is in setting up its call. */
export type Phase =
  | 'idle'
  | 'reading'
  | 'pickup'
  | 'driver'
  | 'confirm'
  | 'creating'
  | 'done';

/** A field of the call open for editing in its card. */
export type EditKey =
  | 'pickup'
  | 'destination'
  | 'vehicle'
  | 'priority'
  | 'towType'
  | 'contactPhone'
  | 'reason'
  | 'dispatchNumber'
  | 'contactName'
  | 'plate'
  | 'vin';

export type Message =
  | { id: string; kind: 'user'; text: string }
  | { id: string; kind: 'assistant'; text: string; muted?: boolean }
  | { id: string; kind: 'card' }
  | { id: string; kind: 'drivers' }
  | { id: string; kind: 'confirm' }
  | { id: string; kind: 'created' };

/** One chat, which is one call. */
export interface Chat {
  id: string;
  /** When it was started: "7:48 AM". */
  timeLabel: string;
  call: DraftCall;
  notes: string[];
  phase: Phase;
  messages: Message[];
  /**
   * The account picked by hand. Left null, the call is on the account the
   * text names, or the company's default.
   */
  accountId: string | null;
  /** The driver's user id; null to leave it unassigned; undefined not yet asked. */
  driverId: string | null | undefined;
  driversOpen: boolean;
  editing: EditKey | null;
  editingNotes: boolean;
  /** Optional fields asked for by their chip, shown though still empty. */
  shown: Partial<Record<EditKey, boolean>>;
  /** Once made: the call's id, when it could be found, and its number. */
  created: { id: string | null; dispatchNumber: string } | null;
  /**
   * How the text was read, once it was: by the backend's parser ('ai') or
   * by the app's own rules ('rules'), the fields the parser filled, and the
   * driver it named (a user id).
   */
  reading: {
    by: 'ai' | 'rules';
    filled: DispatchParseField[];
    suggestedDriverId: string | null;
  } | null;
  /** Fields the dispatcher changed after the reading, by the backend's names. */
  changed: DispatchParseField[];
}

/** A driver the call can go to, from GET /drivers. */
export interface ChatDriver {
  /** The user id, which is what a call's drivers are. */
  userId: string;
  name: string;
  truckNumber: string | null;
  status: string | null;
}

export const firstName = (name: string) => name.split(' ')[0] ?? name;

export const clock = (date = new Date()) => {
  const hour = date.getHours() % 12 || 12;
  const minutes = String(date.getMinutes()).padStart(2, '0');
  return `${hour}:${minutes} ${date.getHours() >= 12 ? 'PM' : 'AM'}`;
};
